/**
 * "Delay: Event" conditions: a path that runs for chosen events of a Delay until event node.
 *
 * The condition is an ordinary comparison. Its left side is the `matched_event_id` variable of
 * one Delay, which holds the id of the event card that ended the wait (empty when the wait timed
 * out). Its right side lists what the path runs for: a card's id, or, for a card that lists
 * alerts, the card's id, a colon and one of its alerts. Ids are saved rather than labels, so
 * editing a card changes its chip everywhere and never breaks a path. Mirrors
 * `backend/logiblocks/graph/delay_event_choice.py` and `delay_event_conditions.py`.
 */
import { VariableSource } from '@/proto/common';
import {
  BoolExprGroup,
  ComparisonOp,
  DelayUntilEvent,
  DelayWaitEvent,
  VariableRef,
} from '@/proto/logiblocks/logiblocks';
import type { GroupedFieldOption } from '@/apis/logiblocksAPI';
import type { ReactFlowNode } from '@/pages/Logiblocks/types';
import { eventSummaryText } from '../delayAction/delayUntilEventModel';

/** The Delay output that holds the id of the event card that ended the wait. */
export const DELAY_EVENT_ID_VARIABLE = 'matched_event_id';

/** The operator saved for "Delay: Event is ...": an exact "is one of" on the card ids. */
export const DELAY_EVENT_OPERATOR = ComparisonOp.COMPARISON_IN_LIST;

/** How the operator reads in the editor and in summaries. */
export const DELAY_EVENT_OPERATOR_LABEL = 'Is';

/** The chip for a chosen event that is no longer on the Delay. */
export const MISSING_DELAY_EVENT_LABEL = 'Event no longer on the Delay';

export const MSG_DELAY_EVENT_MISSING =
  'This path refers to an event that is no longer on the Delay. Choose an event again.';

export interface DelayEventSource {
  node: ReactFlowNode;
  untilEvent: DelayUntilEvent;
}

/** The Delay until event node whose event id `ref` reads, or undefined when `ref` reads anything else. */
export function getDelayEventSource(
  ref: VariableRef | undefined,
  nodes: ReactFlowNode[]
): DelayEventSource | undefined {
  if (
    !ref ||
    ref.variable_name !== DELAY_EVENT_ID_VARIABLE ||
    ref.derived_variable_name !== '' ||
    ref.source !== VariableSource.SOURCE_NODE_TYPE
  ) {
    return undefined;
  }
  const node = nodes.find((n) => n.id === ref.node_id);
  const untilEvent = node?.data.delay_action?.until_event;
  return node && untilEvent ? { node, untilEvent } : undefined;
}

export const isDelayEventRef = (
  ref: VariableRef | undefined,
  nodes: ReactFlowNode[]
): boolean => getDelayEventSource(ref, nodes) !== undefined;

/** Joins a card's id and one of its alerts in what a path saves. */
export const ALERT_SEPARATOR = ':';

/**
 * The alerts a card lists, so that a path can pick one of them. Undefined for a card that lists
 * none: any card but an alert card, or one set to every alert type, which is one entry.
 */
function listedAlerts(event: DelayWaitEvent): string[] | undefined {
  const alerts =
    event.alert_triggered?.alerts ?? event.alert_action_taken?.alerts;
  if (
    !alerts ||
    alerts.select_all_alert_types ||
    alerts.alert_types.length === 0
  ) {
    return undefined;
  }
  return alerts.alert_types;
}

/** What a card says when only one of its alerts counts, for example "Alert triggered: Late pickup". */
function alertEntryText(event: DelayWaitEvent, alert: string): string {
  const only = { alert_types: [alert], select_all_alert_types: false };
  return eventSummaryText({
    ...event,
    ...(event.alert_triggered && {
      alert_triggered: { ...event.alert_triggered, alerts: only },
    }),
    ...(event.alert_action_taken && {
      alert_action_taken: { ...event.alert_action_taken, alerts: only },
    }),
  });
}

interface DelayEventChoice {
  event: DelayWaitEvent;
  /** Set when the path names one alert of the card, and not the card as a whole. */
  alert?: string;
}

/** What a saved choice names on the Delay, or undefined when it is no longer there. */
export function resolveDelayEventChoice(
  untilEvent: DelayUntilEvent,
  chosen: string
): DelayEventChoice | undefined {
  const whole = untilEvent.events.find((e) => e.id === chosen);
  if (whole) return { event: whole };
  const at = chosen.lastIndexOf(ALERT_SEPARATOR);
  if (at < 0) return undefined;
  const event = untilEvent.events.find((e) => e.id === chosen.slice(0, at));
  const alert = chosen.slice(at + 1);
  return event && listedAlerts(event)?.includes(alert)
    ? { event, alert }
    : undefined;
}

/** The label a chosen event shows, or the missing-event label when the Delay no longer has it. */
export function delayEventLabel(
  untilEvent: DelayUntilEvent,
  chosen: string
): string {
  const choice = resolveDelayEventChoice(untilEvent, chosen);
  if (!choice) return MISSING_DELAY_EVENT_LABEL;
  return choice.alert
    ? alertEntryText(choice.event, choice.alert)
    : eventSummaryText(choice.event);
}

/**
 * The picker's options: the Delay's events under the heading "Events on {n}. Delay". A card that
 * lists alerts gives one entry for each alert it selected, and no more. Any other card is one
 * entry. A choice already saved that is not among those stays as an option so its chip is not
 * lost: a whole card saved before alerts could be picked keeps its card text, and an event that
 * is gone reads as missing, not as a raw id.
 */
export function getDelayEventOptions(
  source: DelayEventSource,
  nodes: ReactFlowNode[],
  chosenIds: string[]
): GroupedFieldOption[] {
  const number = nodes.findIndex((n) => n.id === source.node.id) + 1;
  const options = source.untilEvent.events.flatMap((event) => {
    const alerts = listedAlerts(event);
    return alerts
      ? alerts.map((alert) => ({
          value: `${event.id}${ALERT_SEPARATOR}${alert}`,
          label: alertEntryText(event, alert),
        }))
      : [{ value: event.id, label: eventSummaryText(event) }];
  });
  const listed = new Set(options.map((option) => option.value));
  for (const id of chosenIds) {
    if (!listed.has(id)) {
      options.push({
        value: id,
        label: delayEventLabel(source.untilEvent, id),
      });
    }
  }
  return [{ label: `Events on ${number}. Delay`, options }];
}

/** The chosen events, or alerts of one, that are no longer on the Delay a comparison reads, per leaf. */
export function getStaleDelayEventIdsByLeaf(
  group: BoolExprGroup,
  nodes: ReactFlowNode[]
): string[][] {
  return group.expressions.flatMap((expr) => {
    if (expr.group) {
      return getStaleDelayEventIdsByLeaf(expr.group, nodes);
    }
    const comparison = expr.comparison_expr;
    const source = getDelayEventSource(comparison?.variable, nodes);
    if (!comparison || !source) {
      return [[]];
    }
    const chosen = [
      ...(comparison.rhs?.literal ? [comparison.rhs.literal] : []),
      ...(comparison.rhs?.list?.items ?? []),
    ].flatMap((value) =>
      value.string_value !== undefined ? [value.string_value] : []
    );
    return [
      chosen.filter((id) => !resolveDelayEventChoice(source.untilEvent, id)),
    ];
  });
}
